import { CRS } from '@birdi/types/crs';
import { PointReference } from '@birdi/types/ground-references';

export type PlatformSource =
  | { type: 'objective' | 'folder' | 'map'; id: string; name?: string }
  | { type: 'folders'; ids: string[]; name?: string };

export interface GetProcessingJobsFiltersV2 {
  start?: number;
  limit?: number;
  organisationId?: string;
  dates?: [string, string];
  missionId?: string;
  state?: string;
  processingJobName?: string;
  workspaceId?: string;
}

export type MediaExtension = '.jpg' | '.jpeg';
// export type ProcessingStep =
//   'optimize_cameras'
//   | 'build_cloud'
//   | 'export_points'
//   | 'get_outer_boundary'
//   | 'build_DSM'
//   | 'export_DSM'
//   | 'build_orthomosaics'
//   | 'export_orthomosaics'
//   | 'build_DTM'
//   | 'export_DTM'
//   | 'build_hillshade_DSM'
//   | 'build_hillshade_DTM'
//   | 'export_report';

// export interface CRS {
//   label: string;
//   value: number | null;
//   authSrid: number;
//   authName: 'EPSG' | 'BIRDI',
//   wkt: string;
// }

/**
 * Note: we don't use geojson here as crs has been
 * depreciated. Whilst PostGIS still handles it, it
 * is unclear if this will be the case forever.
 */

export type SimpleProcessingConfig = {
  /** currentProcessingConfigVersion version */
  version?: number;
  mechanicalShutter: boolean;
  manualSetup: boolean;
  nadirOnly: boolean;
  outputCoordinateSystem?: CRS;
  outputCoordinateSystemFamily?: string;
  outBoundaryName?: string;
  outBoundaryPolygon?: any | null; // geojson
  orthomosaic: boolean;
  orthomosaicResolution: number | null;
  dsm: boolean;
  dsmResolution: number | null;
  dtm: boolean;
  dtmResolution: number | null;
  contours: boolean;
  contour1Interval: number | null;
  contour2Interval: number | null;
  contour3Interval: number | null;
  pointCloud: boolean;
  texturedMesh: boolean;
  useRTK: boolean;
  rtkCoordinateSystem?: CRS;
  rtkCoordinateSystemFamily?: string;
  useReferences: boolean;
  referenceCoordinateSystem?: CRS;
  refernceCoordinateSystemFamily?: string;
  references: PointReference[];
  /**
   * Reference-set versions the points were pulled from, when they came from the
   * map's site rather than a CSV (BIR-22). Provenance only — the points travel
   * in `references`. The processing callback reads this to decide which prism
   * set an observation belongs to.
   */
  referenceSetVersionIds?: string[];

  // usePrisms: boolean;
  // prismsCoordinateSystem?: CRS;
  // prismsCoordinateSystemFamily?: string;
  // prisms: PointReference[];

  relaxRTK: boolean;
  report: boolean;
  // reverse compatibility
  outputHeightDatum?: string;
  referenceHeightDatum?: string;
  rtkHeightDatum?: string;
};

export type ProcessingPreset = {
  name: string;
  id: string;
  public: boolean;
  config: SimpleProcessingConfig;
};

export type ProcessingPlatformRequest = {
  /** currentProcessingRequestVersion version */
  version?: number;
  presetId: string;
  presetName: string;
  presetAlias: string | null;
  source: PlatformSource;
  target: PlatformSource;
  useLinux: boolean;
  config: SimpleProcessingConfig;
  jobId?: string;
};

export interface ProcessingConfig {
  /** Name of the project */
  name?: string;

  /** Next state for the job to assume */
  nextState?: 'tagging' | 'manual-setup';

  /** Start new project (overwrite if exists) or open created project
   ** Default: true */
  newProject: boolean;
  /** EPSG of the import data. Not used in the current processing ('EPSG::1150' is used as default),
   * will be required to change transformation RTK and non-RTK and GDA94/GDA2020
   *
   * AVAILABLE TRANSFORMATIONS:
   ** 'EPSG::1150' - GDA94 TOWGS84[0,0,0,0,0,0,0]
   ** 'EPSG::9688' - GDA94 TOWGS84[0.06155,-0.01087,-0.04019,0.0394924,0.0327221,0.0328979,-0.009993999999999999]
   ** 'EPSG::8450' - GDA2020 TOWGS84[0,0,0,0,0,0,0]
   */
  // epsg: EPSG;
  // projectFolderIn: ProjectFolderRelativePath; // 'CaptureData',
  // projectFolderOut: ProjectFolderRelativePath; // 'CaptureData',
  // toWGS: 'EPSG::1150'|'';

  /** Set rolling shutter of the cameras to ON or OFF */
  rollingShutterCompensation: boolean;
  // 'gcp_file_name': 'GCPs.csv',
  // 'kml_file_name': 'outerboundary.kml',
  // 'python_log_name': 'api.log',
  // 'project_name': 'project.psx',
  /** Extentions for images */
  mediaExtensions: MediaExtension[];
  // 'chunk_label': 'API_chunk',
  // 'compatible_major_version': '1.8'

  /** Skip duplicate image check */
  skipDuplicatesCheck: boolean;

  /** Processing steps to perform */
  // processingSteps: ProcessingStep[];

  /** Add a list of photos to the chunk. */
  addPhotos: {
    /** load_xmp_accuracy (bool) – Load accuracy from metadata,
     * for RTK set true , for non-RTK set false the accuracy will be set to 10m
     ** Default value: false
     ** Our settings: true (assuming RTK) */
    load_xmp_accuracy: boolean;
    /** load_rpc_txt (bool) – Load satellite RPC data from auxiliary TXT files.
     * for RTK set true, for non-RTK set false
     ** Default value: false
     ** Our settings: true (assuming RTK) */
    load_rpc_txt: boolean;
    /** layout (ImageLayout) – Image layout.
     ** Default value: 'UndefinedLayout' */
    layout?: ImageLayout;
    /** strip_extensions (bool) – Strip file extensions from camera labels.
     ** Default value: true */
    strip_extensions?: boolean;
    /** load_reference (bool) – Load reference coordinates.
     ** Default value: true */
    load_reference?: boolean;
    /** load_xmp_calibration (bool) – Load calibration from XMP meta data.
     ** Default value: true */
    load_xmp_calibration?: boolean;
    /** load_xmp_orientation (bool) – Load orientation from XMP meta data.
     ** Default value: true */
    load_xmp_orientation?: boolean;
    /** load_xmp_antenna (bool) – Load GPS/INS offset from XMP meta data.
     ** Default value: true */
    load_xmp_antenna?: boolean;
  };
  /** Perform image matching for the chunk frame. */
  matchPhotos: {
    /** downscale (int) – Image alignment accuracy.
     ** Default value: 1 */
    downscale: MatchPhotosDownscale;
    /** keypoint_limit (int) – Key point limit.
     ** Default value: 40000 */
    keypoint_limit?: number;
    /** tiepoint_limit (int) – Tie point limit.
     ** Default value: 4000
     ** This parameter needs to be defined for the best outputs, currently used 4,000 - 10,000 */
    tiepoint_limit?: number;
    /** keypoint_limit_per_mpx (int) – Key point limit per megapixel.
     ** Default value: 1000
     ** This parameter needs to be defined for the best outputs, currently used 1,000 - 4,000 */
    keypoint_limit_per_mpx: number;
    /** guided_matching, (bool) – Enable guided image matching.
     ** Default value: false
     ** Our settings: true */ /// TODO: requires more investigation/clarification
    guided_matching: boolean;
    /** filter_stationary_points (bool) – Exclude tie points which are stationary across images.
     ** Default value: true
     ** Our settings: false */ /// TODO: requires more investigation/clarification
    filter_stationary_points?: boolean;
    /** generic_preselection (bool) – Enable generic preselection.
     ** Default value: true */
    generic_preselection?: boolean;
    /** reference_preselection (bool) – Enable reference preselection.
     ** Default value: true */
    reference_preselection?: boolean;
    /** reference_preselection_mode (ReferencePreselectionMode) – Reference preselection mode.
     ** Default value: 'ReferencePreselectionSource' */
    reference_preselection_mode?: ReferencePreselectionMode;
    /** filter_mask (bool) – Filter points by mask.
     ** Default value: false */
    filter_mask?: boolean;
    /** mask_tiepoints (bool) – Apply mask filter to tie points.
     ** Default value: true */
    mask_tiepoints?: boolean;
    /** keep_keypoints (bool) – Store keypoints in the project.
     ** Default value: false */
    keep_keypoints?: boolean;
    /** reset_matches (bool) – Reset current matches.
     ** Default value: false */
    reset_matches?: boolean;
    /** subdivide_task (bool) – Enable fine-level task subdivision.
     ** Default value: true */
    subdivide_task?: boolean;
    /** workitem_size_cameras (int) – Number of cameras in a workitem.
     ** Default value: 20 */
    workitem_size_cameras?: number;
    /** workitem_size_pairs (int) – Number of image pairs in a workitem.
     ** Default value: 80 */
    workitem_size_pairs?: number;
    /** max_workgroup_size (int) – Maximum workgroup size.
     ** Default value: 100 */
    max_workgroup_size?: number;
  };
  /** Flag to import ground control and check point references */
  useGroundReferencePoints: boolean;
  /** Align Cameras */
  alignCameras: {
    /** min_image (int) – Minimum number of point projections.
     ** Default value: 2 */
    min_image?: number;
    /** reset_alignment (bool) – Reset current alignment.
     ** Default value: false */
    reset_alignment?: boolean;
    /** subdivide_task (bool) – Enable fine-level task subdivision.
     ** Default value: true */
    subdivide_task?: true;
    /** adaptive_fitting (bool) – Enable adaptive fitting of distortion coefficients.
     ** Default value: false
     ** Our settings: true */ /// TODO: requires more investigation/clarification
    adaptive_fitting: boolean;
  };
  /** Optimize Cameras */
  optimizeCameras: {
    /** min_image (int) – Minimum number of point projections.
     ** Default value: 2 */
    min_image?: number;
    /** reset_alignment (bool) – Reset current alignment.
     ** Default value: false */
    reset_alignment?: boolean;
    /** subdivide_task – Enable fine-level task subdivision.
     ** Default value: 'true (bool)' */
    subdivide_task?: true;
    /** fit_f (bool) – Enable optimization of focal length coefficient.
     ** Default value: true */
    fit_f?: boolean;
    /** fit_cx (bool) – Enable optimization of X principal point coordinates.
     ** Default value: true */
    fit_cx?: boolean;
    /** fit_cy (bool) – Enable optimization of Y principal point coordinates.
     ** Default value: true */
    fit_cy?: boolean;
    /** fit_b1 (bool) – Enable optimization of aspect ratio.
     ** Default value: false */
    fit_b1?: boolean;
    /** fit_b2 (bool) – Enable optimization of skew coefficient.
     ** Default value: false */
    fit_b2?: boolean;
    /** fit_k1 (bool) – Enable optimization of k1 radial distortion coefficient.
     ** Default value: true */
    fit_k1?: boolean;
    /** fit_k2 (bool) – Enable optimization of k2 radial distortion coefficient.
     ** Default value: true */
    fit_k2?: boolean;
    /** fit_k3 (bool) – Enable optimization of k3 radial distortion coefficient.
     ** Default value: true */
    fit_k3?: boolean;
    /** fit_k4 (bool) – Enable optimization of k4 radial distortion coefficient.
     * Effects calibration of large sensor images.
     ** Default value: false
     ** Our settings value: true */
    /// TODO: set to true after discussion with Ray to cover large sensor cameras
    fit_k4: boolean;
    /** fit_p1 (bool) – Enable optimization of p1 tangential distortion coefficient.
     ** Default value: true */
    fit_p1?: boolean;
    /** fit_p2 (bool) – Enable optimization of p2 tangential distortion coefficient.
     ** Default value: true */
    fit_p2?: boolean;
    /** fit_corrections (bool) – Enable optimization of additional corrections.
     ** Default value: false */
    fit_corrections?: boolean;
    /** adaptive_fitting (bool) – Enable adaptive fitting of distortion coefficients.
     ** Default value: false */
    adaptive_fitting: boolean;
    /// TODO does this go here or in alignCameras? - this parameter exists in both
    /** tiepoint_covariance tiepoint_covariance (bool) – Estimate tie point covariance matrices.
     ** Default value: false */
    tiepoint_covariance?: boolean;
  };
  /** Build Depth Maps */
  buildDepthMaps: {
    /** downscale (int) – Depth map quality.
     ** Default value: 4 (Medium) */
    downscale: BuildDepthMapsDownscale;
    /** filter_mode (FilterMode) – Depth map filtering mode.
     ** Default value: 'MildFiltering' */
    filter_mode: FilterMode;
    /** reuse_depth (bool) – Enable reuse depth maps option.
     ** Default value: false */
    reuse_depth?: boolean;
    /** max_neighbors (int) – Maximum number of neighbor images to use for depth map generation.
     ** Default value: 16 */
    max_neighbors?: number;
    /** subdivide_task (bool) – Enable fine-level task subdivision.
     ** Default value: true */
    subdivide_task?: boolean;
    /** workitem_size_cameras (int) – Number of cameras in a workitem.
     ** Default value: 20 */
    workitem_size_cameras?: number;
    /** max_workgroup_size (int) – Maximum workgroup size.
     ** Default value: 100 */
    max_workgroup_size?: number;
  };
  /** Build Model */
  buildModel: {
    /** source_data (DataSource) – Selects between dense point cloud
     ** Default value: 'DenseCloudData'
     ** Our settings: 'DepthMapsData' */
    source_data: SourceData;
    /** surface_type (SurfaceType) – Type of object to be reconstructed.
     ** Default value: 'Arbitrary' */
    surface_type?: SurfaceType;
    /** interpolation (Interpolation) – Interpolation mode.
     ** Default value: 'EnabledInterpolation' */
    interpolation?: Extrapolation;
    /** face_count (FaceCount) – Target face count.
     ** Default value: 'HighFaceCount' */
    face_count?: FaceCount;
    /** face_count_custom (int) – Custom face count.
     ** Default value: 200000 */
    face_count_custom?: number;
    /** vertex_colors (bool) – Enable vertex colors calculation.
     ** Default value: true */
    vertex_colors?: boolean;
    /** vertex_confidence (bool) – Enable vertex confidence calculation.
     ** Default value: true */
    vertex_confidence?: boolean;
    /** volumetric_masks (bool) – Enable strict volumetric masking.
     ** Default value: false */
    volumetric_masks?: boolean;
    /** keep_depth (bool) – Enable store depth maps option.
     ** Default value: true */
    keep_depth?: boolean;
    /** trimming_radius (int) – Trimming radius (no trimming if zero).
     ** Default value: 10 */
    trimming_radius?: number;
    /** subdivide_task (bool) – Enable fine-level task subdivision.
     ** Default value: true */
    subdivide_task?: boolean;
    /** workitem_size_cameras (int) – Number of cameras in a workitem.
     ** Default value: 20 */
    workitem_size_cameras?: number;
    /** max_workgroup_size (int) – Maximum workgroup size.
     ** Default value: 100 */
    max_workgroup_size?: number;
  };
  /** Build UV.
   ** not used in processing */
  buildUV: {
    /** mapping_mode (MappingMode) – Texture mapping mode.
     ** Default value: 'GenericMapping' */
    mapping_mode?: MappingMode;
    /** page_count (int) – Number of texture pages to generate.
     ** Default value: 1 */
    page_count?: number;
    /** texture_size (int) – Expected size of texture page at texture generation step.
     ** Default value: 8192 */
    texture_size?: number;
  };
  /** Build Textures
   ** not used in processing */
  buildTexture: {
    /** 0 source_model (int) – Source model.
     ** Default value: 0 */
    source_model?: number;
    /** blending_mode (BlendingMode) – Texture blending mode.
     ** Default value: 'MosaicBlending' */
    blending_mode?: BlendingMode;
    /** texture_size (int) – Texture page size.
     ** Default value: 8192 */
    texture_size?: number;
    /** fill_holes (bool) – Enable hole filling.
     ** Default value: true */
    fill_holes?: boolean;
    /** ghosting_filter (bool) – Enable ghosting filter.
     ** Default value: true */
    ghosting_filter?: boolean;
    /** texture_type (Model.TextureType) – Texture type.
     ** Default value: 'DiffuseMap' */
    texture_type?: TextureType;
    /** transfer_texture (bool) – Transfer texture.
     ** Default value: true */
    transfer_texture?: boolean;
  };
  /** Build Dense Cloud */
  buildDenseCloud: {
    /** point_confidence (bool) – Enable point confidence calculation.
     ** Default value: false
     ** Our settings value: true. */
    point_confidence: boolean;
    /** keep_depth (bool) – Enable store depth maps option.
     ** Default value: true */
    keep_depth: boolean;
    /** Non-agisoft parameter. A list of confidence levels (0 - 256) to remove. 0-9 is usually
     * enough as the scale isn't linear */
    filter_confidence?: number[]; // TODO document and type rather than number
    /** point_colors (bool) – Enable point colors calculation.
     ** Default value: true */
    point_colors?: boolean;
    /** max_neighbors (int) – Maximum number of neighbor images to use for depth map filtering.
     ** Default value: 100 */
    max_neighbors?: number;
    /** subdivide_task (bool) – Enable fine-level task subdivision.
     ** Default value: true */
    subdivide_task?: boolean;
    /** workitem_size_cameras (int) – Number of cameras in a workitem.
     ** Default value: 20 */
    workitem_size_cameras?: number;
    /** max_workgroup_size (int) – Maximum workgroup size.
     ** Default value: 10 */
    max_workgroup_size?: number;
  };
  /** Buiuld DEM DSM */
  buildDemDSM: {
    /** source_data (DataSource) – Selects between dense point cloud and tie points.
     ** Default value: 'DenseCloudData'
     ** Our settings: 'DenseCloudData' */
    source_data: SourceData;
    /** classes - (array) Indexes of classes of Dense Cloud to use for DEM building
     ** Our settings: [0] (All Created) to use all classes */
    classes: CloudClasses[];
    /** interpolation (Interpolation) – Interpolation mode.
     ** Default value: 'EnabledInterpolation' */
    interpolation?: Extrapolation;
    /** flip_x (bool) – Flip X axis direction.
     ** Default value: false */
    flip_x?: boolean;
    /** flip_y (bool) – Flip Y axis direction.
     ** Default value: false */
    flip_y?: boolean;
    /** flip_z (bool) – Flip Z axis direction.
     ** Default value: false */
    flip_z?: boolean;
    /** resolution (float) – Output resolution in meters.
     ** Default value: 0 */
    resolution?: number;
    /** subdivide_task (bool) – Enable fine-level task subdivision.
     ** Default value: true */
    subdivide_task?: boolean;
    /** workitem_size_tiles (int) – Number of tiles in a workitem.
     ** Default value: 10 */
    workitem_size_tiles?: number;
    /** max_workgroup_size (int) – Maximum workgroup size.
     ** Default value: 100 */
    max_workgroup_size?: number;
  };
  /** Build contours for the chunk (Agisoft function).
   ** not used in processing */
  buildContours: {
    /** source_data (DataSource) – Selects between dense point cloud and tie points.
     ** Default value: 'ElevationData' */
    source_data: SourceData;
    /** prevent_intersections (bool) – Prevent contour intersections.
     ** Default value: true */
    prevent_intersections?: boolean;
    /** interval (float) – Contour interval.
     ** Default value: 1
     ** Not used in processing, set from array contour_formats */
    interval?: number;
    /** interval (float) – Contour interval.
     ** not Agisoft parameter, replaces interval
     ** Our settings: array of the intervalls is passed: [0.5; 1; 2]; */
    contour_intervals: number[];
    /** Overwrite existing contour shapes
     ** not Agisoft parameter,
     ** Our settings: default false */
    contour_replace: boolean;
    /** min_value min_value (float) – Minimum value of contour range.
     ** Default value: '-1e+10' */
    min_value?: number;
    /** max_value max_value (float) – Maximum value of contour range.
     ** Default value: '1e+10' */
    max_value?: number;
  };
  /** Build contours for the chunk with GDAL */
  buildContoursGDAL: {
    /** interval (float) – Contour interval.
     ** Our settings: array of the intervalls is passed: [0.5; 1; 2]; */
    contour_intervals: number[];
    /** Output formats for contours
     ** Our settings: array of extentions is passed: ['geojson', 'dxf', 'shp] */
    contour_formats: ShapeFormat[];
    /** At the moment only supports Gaussian filterring with different sigma */
    method: 'gaussian';
    /** Amount of Gaussian filterring for DEM to produce contours
     ** default value: 9 */
    sigma: Sigma;
    /** Simplify contours further is set to true (may result in intersecting contours)
     ** Our settings: false */
    simplification: boolean;
    /** Clip the final contours by provided outerboundary.kml
     ** Our settings: true */
    clip_to_project_boundary: boolean;
  };
  /** Build Ortomosaic */
  buildOrthomosaic: {
    /** surface_data = ModelData, surface_data (DataSource) – Orthorectification surface.
     ** Default value: 'ModelData'
     ** Our settings value: 'ElevationData' */
    surface_data: SourceData;
    /** cull_faces (bool) – Enable back-face culling.
     ** Default value: false
     ** Our settings value: true */
    cull_faces: boolean; /// TODO: requires more investigation/clarification
    /** ghosting_filter (bool) – Enable ghosting filter.
     ** Default value: false
     ** Our settings value: true */
    ghosting_filter: boolean;
    /** refine_seamlines (bool) – Refine seamlines based on image content.
     ** Default value: false
     ** Our settings value: true */ /// TODO: requires more investigation/clarification
    refine_seamlines: boolean;
    /** blending_mode blending_mode (BlendingMode) – Orthophoto blending mode.
     ** Default value: 'MosaicBlending' */
    blending_mode?: BlendingMode;
    /** fill_holes fill_holes (bool) – Enable hole filling.
     ** Default value: true */
    fill_holes?: boolean;
    /** resolution resolution (float) – Pixel size in meters.
     ** Default value: 0 */
    resolution?: number;
    /** resolution_x resolution_x (float) – Pixel size in the X dimension in projected units.
     ** Default value: 0 */
    resolution_x?: number;
    /** resolution_y resolution_y (float) – Pixel size in the Y dimension in projected units.
     ** Default value: 0 */
    resolution_y?: number;
    /** flip_x (bool) – Flip X axis direction.
     ** Default value: false */
    flip_x?: boolean;
    /** flip_y (bool) – Flip Y axis direction.
     ** Default value: false */
    flip_y?: boolean;
    /** flip_z (bool) – Flip Z axis direction.
     ** Default value: false */
    flip_z?: boolean;
    /** subdivide_task (bool) – Enable fine-level task subdivision.
     ** Default value: true */
    subdivide_task?: boolean;
    /** workitem_size_cameras (int) – Number of cameras in a workitem.
     ** Default value: 20 */
    workitem_size_cameras?: number;
    /** workitem_size_tiles (int) – Number of tiles in a workitem.
     ** Default value: 10 */
    workitem_size_tiles?: number;
    /** max_workgroup_size (int) – Maximum workgroup size.
     ** Default value: 100 */
    max_workgroup_size?: number;
  };
  /** Classify Ground Points */
  classifyGroundPoints: {
    /** max_distance (float) – Maximum distance (meters).
     ** Default value: 1 */
    max_distance: number;
    /** cell_size (float) – Cell size (meters).
     ** Default value: 50 */
    cell_size: number;
    /** max_angle (float) – Maximum angle (degrees).
     ** Default value: 15
     ** This parameter needs to be defined for the best outputs, currently used 15.0 - 25.0 */
    max_angle: number;
    /** erosion_radius (float) – Erosion radius (meters).
     ** Default value: 0 */
    erosion_radius?: number;
    /** source (PointClass) – Class of points to be re-classified.
     ** Default value: 0 (all) */
    source?: number;
  };
  /** Build DEM DTM */
  buildDemDTM: {
    /** source_data (DataSource) – Selects between dense point cloud and tie points.
     ** Default value: 'DenseCloudData' */
    source_data: SourceData;
    /** Classes to use for classification
     ** Our settings: [2] (Ground) */
    classes: CloudClasses[];
    /** interpolation (Interpolation) – Interpolation mode.
     ** Default value: 'EnabledInterpolation' */
    interpolation?: Extrapolation;
    /** flip_x (bool) – Flip X axis direction.
     ** Default value: false */
    flip_x?: boolean;
    /** flip_y (bool) – Flip Y axis direction.
     ** Default value: false */
    flip_y?: boolean;
    /** flip_z (bool) – Flip Z axis direction.
     ** Default value: false */
    flip_z?: boolean;
    /** resolution (float) – Output resolution in meters.
     ** Default value: 0 */
    resolution?: number;
    /** subdivide_task (bool) – Enable fine-level task subdivision.
     ** Default value: true */
    subdivide_task?: boolean;
    /** workitem_size_tiles (int) – Number of tiles in a workitem.
     ** Default value: 10 */
    workitem_size_tiles?: number;
    /** max_workgroup_size (int) – Maximum workgroup size.
     ** Default value: 100 */
    max_workgroup_size?: number;
  };
  /** GDAL Hillshade Export
   *
   * https: //gdal.org/programs/gdaldem.html */
  exportHillshade: {
    /** format --- output format ('GTiff', etc...)
     ** Our settings: 'GTiff' is used */
    format: 'GTiff';
    /** vertical exaggeration used to pre-multiply the elevations.
     ** Our settings: 1 is used */
    z_factor: number;
    /** ratio of vertical units to horizontal.
     ** Default value: 1 */
    scale: number;
    /** azimuth of the light, in degrees. 0 if it comes from the top of the raster,
     * 90 from the east, ... The default value, 315, should rarely be changed as
     * it is the value generally used to generate shaded maps.
     ** Default value: 315 */
    azimuth: number;
    /** altitude of the light, in degrees. 90 if the light comes from above the DEM,
     * 0 if it is raking light.
     ** Default value: 45 */
    altitude: number;
    /** GDAL parameters for compression and tiles in tiff creation
     * Our settings: [ COMPRESS = JPEG; JPEG_QUALITY = 100; TILED = YES;
     * BLOCKXSIZE = 512; BLOCKYSIZE = 512 ] */
    creation_options: string[];
  };
  /** Export contours layer to file.
   ** not used in processing */
  exportShapesContours: {
    /** save_polylines (bool) – Export polylines.
     ** Default value: false
     ** Our settings: true; */
    save_polylines: boolean;
    /** polygons_as_polylines (bool) – Save polygons as polylines.
     ** Default value: false
     ** Our settings: true */
    polygons_as_polylines: boolean;
    /** save_polygons (bool) – Export polygons.
     ** Default value: false */
    save_polygons: boolean;
    /** save_points (bool) – Export points.
     ** Default value: false */
    save_points?: boolean;
    /** format (ShapesFormat) – Export format.
     ** Default value: 'ShapesFormatNone'
     ** Not used in processing, set as array in buildContours */
    format?: ShapeFormat;
    /** Output formats for contours
     ** not Agisoft parameter, replaces 'format'
     ** Our settings: array of extentions is passed: ['geojson', 'dxf'] */
    contour_formats: ShapeFormat[];
    /** save_labels (bool) – Export labels.
     ** Default value: true */
    save_labels?: boolean;
    /** save_attributes (bool) – Export attributes.
     ** Default value: true */
    save_attributes?: boolean;
  };
  /** Export point cloud. */
  exportPoints: {
    /** source_data (DataSource) – Selects between dense point cloud and tie points.
     * If not specified, uses dense cloud if available.
     ** Default value: 'DenseCloudData' */
    source_data: SourceData;
    /** format (PointsFormat) – Export format.
     ** Default value: 'PointsFormatNone'
     ** Our settings: 'las' */
    format: PointFormat;
    /** binary (bool) – Enables/disables binary encoding for selected format (if applicable).
     ** Default value: true */
    binary?: boolean;
    /** save_normals save_normals (bool) – Enables/disables export of point normals.
     ** Default value: true */
    save_normals?: boolean;
    /** save_colors (bool) – Enables/disables export of point colors.
     ** Default value: true */
    save_colors?: boolean;
    /** save_classes (bool) – Enables/disables export of point classes.
     ** Default value: true */
    save_classes?: boolean;
    /** save_confidence (bool) – Enables/disables export of point confidence.
     ** Default value: true */
    save_confidence?: boolean;
    /** raster_transform (RasterTransformType) – Raster band transformation.
     ** Default value: 'RasterTransformNone' */
    raster_transform?: RasterTransform;
    /** colors_rgb_8bit (bool) – Convert colors to 8 bit RGB.
     ** Default value: true */
    colors_rgb_8bit?: boolean;
    /** comment c (string) – Optional comment (if supported by selected format).
     ** Default value: '' */
    comment?: string;
    /** save_comment (bool) – Enable comment export.
     ** Default value: true */
    save_comment?: boolean;
    /** image_format (ImageFormat) – Image data format.
     ** Default value: 'ImageFormatJPEG' */
    image_format?: ImageFormat;
    /** clip_to_boundary (bool) – Clip point cloud to boundary shapes.
     ** Default value: true */
    clip_to_boundary?: boolean;
    /** block_width (float) – Block width in meters.
     ** Default value: 1000 */
    block_width?: number;
    /** block_height (float) – Block height in meters.
     ** Default value: 1000 */
    block_height?: number;
    /** split_in_blocks (bool) – Enable tiled export.
     ** Default value: false */
    split_in_blocks?: boolean;
    /** save_images (bool) – Enable image export.
     ** Default value: false */
    save_images?: boolean;
    /** compression (bool) – Enable compression (Cesium format only).
     ** Default value: true */
    compression?: boolean;
    /** screen_space_error (float) – Target screen space error (Cesium format only).
     ** Default value: 16 */
    screen_space_error?: number;
    /** subdivide_task (bool) – Enable fine-level task subdivision.
     ** Default value: true */
    subdivide_task?: boolean;
  };
  /** Export DEM DSM file */
  exportRasterDSM: {
    /** raster_transform (RasterTransformType) – Raster band transformation.
     ** Default value: 'RasterTransformNone' */
    raster_transform: RasterTransform;
    /** source_data (DataSource) – Selects between DEM and orthomosaic.
     ** Default value: 'OrthomosaicData'
     ** Our settings: 'ElevationData' */
    source_data: SourceData;
    /** image_format (ImageFormat) – Tile format.
     ** Default value: 'ImageFormatNone'
     ** Our settings: 'ImageFormatTIFF' */
    image_format: ImageFormat;
    /** resolution (float) – Output resolution in meters.
     ** Default value: 0 (use source resolution)
     ** Our settings: 0.1 */
    resolution?: number;
    /** save_alpha (bool) – Enable alpha channel generation.
     ** Default value: true
     ** Our settings: true */
    save_alpha?: boolean;
    /** nodata_value (float) – No-data value (DEM export only).
     ** Default value: -32767 */
    nodata_value?: number;
    /** format (RasterFormat) – Export format.
     ** Default value: 'RasterFormatTiles' */
    format?: RasterFormat;
    /** resolution_x (float) – Pixel size in the X dimension in projected units.
     ** Default value: 0 */
    resolution_x?: number;
    /** resolution_y (float) – Pixel size in the Y dimension in projected units.
     ** Default value: 0 */
    resolution_y?: number;
    /** block_width (int) – Raster block width in pixels.
     ** Default value: 10000 */
    block_width?: number;
    /** block_height (int) – Raster block height in pixels.
     ** Default value: 10000 */
    block_height?: number;
    /** split_in_blocks (bool) – Split raster in blocks.
     ** Default value: false */
    split_in_blocks?: boolean;
    /** width (int) – Raster width.
     ** Default value: 0 */
    width?: number;
    /** height (int) – Raster height.
     ** Default value: 0 */
    height?: number;
    /** save_kml (bool) – Enable kml file generation.
     ** Default value: false */
    save_kml?: boolean;
    /** save_world (bool) – Enable world file generation.
     ** Default value: false */
    save_world?: boolean;
    /** save_scheme (bool) – Enable tile scheme files generation.
     ** Default value: false */
    save_scheme?: boolean;
    /** image_description (string) – Optional description to be added to image files.
     ** Default value: '' */
    image_description?: string;
    /** network_links (bool) – Enable network links generation for KMZ format.
     ** Default value: true */
    network_links?: boolean;
    /** min_zoom_level (int) – Minimum zoom level (Google Map Tiles
     ** Default value: -1 */
    min_zoom_level?: number;
    /** max_zoom_level (int) – Maximum zoom level (Google Map Tiles
     ** Default value: -1 */
    max_zoom_level?: number;
    /** white_background (bool) – Enable white background.
     ** Default value: true */
    white_background?: boolean;
    /** clip_to_boundary (bool) – Clip raster to boundary shapes.
     ** Default value: true */
    clip_to_boundary?: boolean;
    /** title (string) – Export title.
     ** Default value: 'Orthomosaic'' */
    title?: string;
    /** description (string) – Export description.
     ** Default value: ''Generated by Agisoft Metashape'' */
    description?: string;
    /** north_up (bool) – Use north-up orientation for export.
     ** Default value: true */
    north_up?: boolean;
    /** tile_width (int) – Tile width in pixels.
     ** Default value: 256 */
    tile_width?: number;
    /** tile_height (int) – Tile height in pixels.
     ** Default value: 25 */
    tile_height?: number;
    /** image_compression (ImageCompression) – Image compression parameters. */
    image_compression?: {
      /** image_compression (ImageCompression) – Image compression parameters.
       ** Our settings: 'TiffCompressionJPEG' */
      tiff_compression: TiffCompression;
      /** Quality of jpeg compression
       ** Our settings: 100 */
      jpeg_quality: number;
      /** Enable image pyramid deneration for TIFF files.
       ** Our settings: true */
      tiff_overviews: boolean;
      /** Export tiled TIFF.
       ** Our settings: true */
      tiff_tiled: boolean;
    };
  };
  /** Export DEM DTM file */
  exportRasterDTM: {
    /** raster_transform (RasterTransformType) – Raster band transformation.
     ** Default value: 'RasterTransformNone' */
    raster_transform: RasterTransform;
    /** source_data (DataSource) – Selects between DEM and orthomosaic.
     ** Default value: 'OrthomosaicData'
     ** Our settings: 'ElevationData' */
    source_data: SourceData;
    /** image_format (ImageFormat) – Tile format.
     ** Default value: 'ImageFormatNone'
     ** Our settings: 'ImageFormatTIFF' is used */
    image_format: ImageFormat;
    /** resolution (float) – Output resolution in meters.
     ** Default value: 0 (use source resolution)
     ** Our settings: 0.1 */
    resolution?: number;
    /** save_alpha (bool) – Enable alpha channel generation.
     ** Default value: true
     ** Our settings: true */
    save_alpha?: boolean;
    /** nodata_value (float) – No-data value (DEM export only).
     ** Default value: -32767 */
    nodata_value?: number;
    /** format (RasterFormat) – Export format.
     ** Default value: 'RasterFormatTiles' */
    format?: RasterFormat;
    /** resolution_x (float) – Pixel size in the X dimension in projected units.
     ** Default value: 0 */
    resolution_x?: number;
    /** resolution_y (float) – Pixel size in the Y dimension in projected units.
     ** Default value: 0 */
    resolution_y?: number;
    /** block_width (int) – Raster block width in pixels.
     ** Default value: 10000 */
    block_width?: number;
    /** block_height (int) – Raster block height in pixels.
     ** Default value: 10000 */
    block_height?: number;
    /** split_in_blocks (bool) – Split raster in blocks.
     ** Default value: false */
    split_in_blocks?: boolean;
    /** width (int) – Raster width.
     ** Default value: 0 */
    width?: number;
    /** height (int) – Raster height.
     ** Default value: 0 */
    height?: number;
    /** save_kml (bool) – Enable kml file generation.
     ** Default value: false */
    save_kml?: boolean;
    /** save_world (bool) – Enable world file generation.
     ** Default value: false */
    save_world?: boolean;
    /** save_scheme (bool) – Enable tile scheme files generation.
     ** Default value: false */
    save_scheme?: boolean;
    /** image_description (string) – Optional description to be added to image files.
     ** Default value: '' */
    image_description?: string;
    /** network_links (bool) – Enable network links generation for KMZ format.
     ** Default value: true */
    network_links?: boolean;
    /** min_zoom_level (int) – Minimum zoom level (Google Map Tiles
     ** Default value: -1 */
    min_zoom_level?: number;
    /** max_zoom_level (int) – Maximum zoom level (Google Map Tiles
     ** Default value: -1 */
    max_zoom_level?: number;
    /** white_background (bool) – Enable white background.
     ** Default value: true */
    white_background?: boolean;
    /** clip_to_boundary (bool) – Clip raster to boundary shapes.
     ** Default value: true */
    clip_to_boundary?: boolean;
    /** title (string) – Export title.
     ** Default value: ''Orthomosaic'' */
    title?: string;
    /** description (string) – Export description.
     ** Default value: ''Generated by Agisoft Metashape'' */
    description?: string;
    /** north_up (bool) – Use north-up orientation for export.
     ** Default value: true */
    north_up?: boolean;
    /** tile_width (int) – Tile width in pixels.
     ** Default value: 256 */
    tile_width?: number;
    /** tile_height (int) – Tile height in pixels.
     ** Default value: 25 */
    tile_height?: number;
    /** image_compression (ImageCompression) – Image compression parameters. */
    image_compression?: {
      /** image_compression (ImageCompression) – Image compression parameters.
       ** Our settings: 'TiffCompressionJPEG' */
      tiff_compression: TiffCompression;
      /** Quality of jpeg compression
       ** Our settings: 100 */
      jpeg_quality: number;
      /** Enable image pyramid deneration for TIFF files.
       ** Our settings: true */
      tiff_overviews: boolean;
      /** Export tiled TIFF.
       ** Our settings: true */
      tiff_tiled: boolean;
    };
  };
  /** Export Orthomosaics file */
  exportRasterOrthomosaic: {
    /** raster_transform (RasterTransformType) – Raster band transformation.
     ** Default value: 'RasterTransformNone' */
    raster_transform?: RasterTransform;
    /** source_data (DataSource) – Selects between DEM and orthomosaic.
     ** Default value: 'OrthomosaicData'
     ** Our settings: 'OrthomosaicData' */
    source_data: SourceData;
    /** image_format (ImageFormat) – Tile format.
     ** Default value: 'ImageFormatNone'
     ** Our settings: 'ImageFormatTIFF' is used */
    image_format: ImageFormat;
    /** resolution (float) – Output resolution in meters.
     ** Default value: 0 (use source resolution)
     ** Our settings: 0.1 */
    resolution?: number;
    /** save_alpha (bool) – Enable alpha channel generation.
     ** Default value: true
     ** Our settings: true */
    save_alpha?: boolean;
    /** nodata_value (float) – No-data value (DEM export only).
     ** Default value: -32767 */
    nodata_value?: number;
    /** format (RasterFormat) – Export format.
     ** Default value: 'RasterFormatTiles' */
    format?: RasterFormat;
    /** resolution_x (float) – Pixel size in the X dimension in projected units.
     ** Default value: 0 */
    resolution_x?: number;
    /** resolution_y (float) – Pixel size in the Y dimension in projected units.
     ** Default value: 0 */
    resolution_y?: number;
    /** block_width (int) – Raster block width in pixels.
     ** Default value: 10000 */
    block_width?: number;
    /** block_height (int) – Raster block height in pixels.
     ** Default value: 10000 */
    block_height?: number;
    /** split_in_blocks (bool) – Split raster in blocks.
     ** Default value: false */
    split_in_blocks?: boolean;
    /** width (int) – Raster width.
     ** Default value: 0 */
    width?: number;
    /** height (int) – Raster height.
     ** Default value: 0 */
    height?: number;
    /** save_kml (bool) – Enable kml file generation.
     ** Default value: false */
    save_kml?: boolean;
    /** save_world (bool) – Enable world file generation.
     ** Default value: false */
    save_world?: boolean;
    /** save_scheme (bool) – Enable tile scheme files generation.
     ** Default value: false */
    save_scheme?: boolean;
    /** image_description (string) – Optional description to be added to image files.
     ** Default value: '' */
    image_description?: string;
    /** network_links (bool) – Enable network links generation for KMZ format.
     ** Default value: true */
    network_links?: boolean;
    /** min_zoom_level (int) – Minimum zoom level (Google Map Tiles
     ** Default value: -1 */
    min_zoom_level?: number;
    /** max_zoom_level (int) – Maximum zoom level (Google Map Tiles
     ** Default value: -1 */
    max_zoom_level?: number;
    /** white_background (bool) – Enable white background.
     ** Default value: true */
    white_background?: boolean;
    /** clip_to_boundary (bool) – Clip raster to boundary shapes.
     ** Default value: true */
    clip_to_boundary?: boolean;
    /** title (string) – Export title.
     ** Default value: ''Orthomosaic'' */
    title?: string;
    /** description (string) – Export description.
     ** Default value: ''Generated by Agisoft Metashape'' */
    description?: string;
    /** north_up (bool) – Use north-up orientation for export.
     ** Default value: true */
    north_up?: boolean;
    /** tile_width (int) – Tile width in pixels.
     ** Default value: 256 */
    tile_width?: number;
    /** tile_height (int) – Tile height in pixels.
     ** Default value: 25 */
    tile_height?: number;
    /** image_compression (ImageCompression) – Image compression parameters. */
    image_compression?: {
      /** image_compression (ImageCompression) – Image compression parameters.
       ** Our settings: 'TiffCompressionJPEG' */
      tiff_compression: TiffCompression;
      /** Quality of jpeg compression
       ** Our settings: 100 */
      jpeg_quality: number;
      /** Enable image pyramid deneration for TIFF files.
       ** Our settings: true */
      tiff_overviews: boolean;
      /** Export tiled TIFF.
       ** Our settings: true */
      tiff_tiled: boolean;
    };
  };
  /** Export pdf report */
  exportReport: {};

  postProcessExport: {};
}

/// TYPES

// export type EPSG =
//  | 'EPSG::4326' /// WGS84
//  | 'EPSG::4283' /// GDA94
//  | 'EPSG::7844'; /// GDA2020

export enum BuildDepthMapsDownscale {
  'Ultra' = 1,
  'High' = 2,
  'Medium' = 4,
  'Low' = 8,
  'Lowest' = 16,
}

export enum MatchPhotosDownscale {
  'Highest' = 0,
  'High' = 1,
  'Medium' = 2,
  'Low' = 4,
  'Lowest' = 8,
}

export type SourceData =
  | 'PointCloudData'
  | 'DenseCloudData'
  | 'DepthMapsData'
  | 'ModelData'
  | 'TiledModelData'
  | 'ElevationData'
  | 'OrthomosaicData'
  | 'ImagesData';

export type FilterMode =
  | 'NoFiltering'
  | 'MildFiltering'
  | 'ModerateFiltering'
  | 'AggressiveFiltering';

export type ReferenceFormat = 'csv';

export type ShapeFormat =
  | 'None'
  | 'shp'
  | 'kml'
  | 'dxf'
  | 'geojson'
  | 'gpkg'
  | 'csv';

export type ImageFormat =
  | 'ImageFormatNone'
  | 'ImageFormatJPEG'
  | 'ImageFormatTIFF'
  | 'ImageFormatPNG'
  | 'ImageFormatBMP'
  | 'ImageFormatEXR'
  | 'ImageFormatPNM'
  | 'ImageFormatSGI'
  | 'ImageFormatCR2'
  | 'ImageFormatSEQ'
  | 'ImageFormatBIL'
  | 'ImageFormatXYZ'
  | 'ImageFormatARA'
  | 'ImageFormatTGA'
  | 'ImageFormatDDS'
  | 'ImageFormatJP2'
  | 'ImageFormatWebP';

export type TiffCompression =
  | 'TiffCompressionDeflate'
  | 'TiffCompressionJPEG'
  | 'TiffCompressionLZW'
  | 'TiffCompressionNone'
  | 'TiffCompressionPackbits';

export type PointFormat = 'None' | 'las';

export type RasterTransform = 'RasterTransformNone' | 'RasterTransformPalette';

export enum CloudClasses {
  'Created' = 0,
  'Unclassified',
  'Ground',
  'LowVegetation',
  'MediumVegetation',
  'HighVegetation',
  'Building',
  'LowPoint',
  'ModelKeyPoint',
  'Water',
  'OverlapPoints' = 12,
}

export type Sigma = 3 | 5 | 7 | 9 | 13;

export type Extrapolation =
  | 'DisabledInterpolation'
  | 'EnabledInterpolation'
  | 'Extrapolated';

export type FaceCount =
  | 'LowFaceCount'
  | 'MediumFaceCount'
  | 'HighFaceCount'
  | 'CustomFaceCount';

export type ImageLayout =
  | 'UndefinedLayout'
  | 'FlatLayout'
  | 'MultiframeLayout'
  | 'MultiplaneLayout';

export type ReferencePreselectionMode =
  | 'ReferencePreselectionSource'
  | 'ReferencePreselectionEstimated'
  | 'ReferencePreselectionSequential';

export type SurfaceType = 'Arbitrary' | 'HeightField';

export type MappingMode =
  | 'GenericMapping'
  | 'OrthophotoMapping'
  | 'AdaptiveOrthophotoMapping'
  | 'SphericalMapping'
  | 'CameraMapping';

export type BlendingMode =
  | 'AverageBlending'
  | 'MosaicBlending'
  | 'MinBlending'
  | 'MaxBlending'
  | 'DisabledBlending';

export type RasterFormat =
  | 'RasterFormatNone'
  | 'RasterFormatTiles'
  | 'RasterFormatKMZ'
  | 'RasterFormatXYZ'
  | 'RasterFormatMBTiles'
  | 'RasterFormatWW'
  | 'RasterFormatTMS'
  | 'RasterFormatGeoPackage';

export type TextureType = 'DiffuseMap';

export interface SearchProcessingJobResult {
  id: string;
  name: string;
  outputs: number;
  endTime: string | null;
  thumbnail: string | null;
  site: string | null;
  organisation?: {
    id: string;
    name: string;
    plan: string | null;
    domain: string | null;
  };
  requestedBy: {
    id: string;
    fname: string;
    lname: string;
  } | null;
}
